/**
 * Frontend Analytics — self-initializing collector.
 *
 * Sends page loads, route changes, Web Vitals, and JS errors to the
 * Bun_Server `/api/frontend/events` endpoint. Auto-starts on import.
 *
 * Disable with: localStorage.setItem('wf_analytics_off', '1');
 */

import { baseURL } from '../config/config';

const ENDPOINT = (baseURL || '') + '/api/frontend/events';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 20;

const disabled =
    typeof window === 'undefined' ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('wf_analytics_off') === '1');

const queue = [];
let flushTimer = null;

function enqueue(type, data) {
    if (disabled) return;
    queue.push({ type, timestamp: Date.now(), data: { ...data, url: data.url || window.location.pathname } });
    if (queue.length >= MAX_BATCH) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

function flush() {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (!queue.length) return;
    const events = queue.splice(0, queue.length);
    const payload = JSON.stringify({ events });
    try {
        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(ENDPOINT, blob);
            return;
        }
    } catch (e) { /* fall through to fetch */ }
    try {
        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
            credentials: 'omit',
        }).catch(() => { /* drop on failure */ });
    } catch (e) { /* never break the app */ }
}

function trackPageLoad() {
    if (disabled) return;
    if (!window.performance || !performance.timing) return;
    const fire = () => {
        try {
            const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
            let ttfb, domReady, fullLoad;
            if (nav) {
                ttfb     = Math.round(nav.responseStart);
                domReady = Math.round(nav.domContentLoadedEventEnd);
                fullLoad = Math.round(nav.loadEventEnd || nav.duration);
            } else {
                const t = performance.timing;
                ttfb     = t.responseStart - t.navigationStart;
                domReady = t.domContentLoadedEventEnd - t.navigationStart;
                fullLoad = t.loadEventEnd - t.navigationStart;
            }
            const fcpEntry = performance.getEntriesByType && performance.getEntriesByType('paint')
                .find(e => e.name === 'first-contentful-paint');
            const fcp = fcpEntry ? Math.round(fcpEntry.startTime) : null;
            enqueue('pageLoad', { ttfb, domReady, fullLoad, fcp });
        } catch (e) { /* no-op */ }
    };
    if (document.readyState === 'complete') setTimeout(fire, 0);
    else window.addEventListener('load', () => setTimeout(fire, 0), { once: true });
}

function trackWebVitals() {
    if (disabled) return;
    let mod;
    try { mod = require('web-vitals'); } catch (e) { return; }
    const send = (m) => enqueue('webVital', { name: m.name, value: Math.round(m.value * 1000) / 1000, rating: m.rating || null });
    try {
        if (mod.onLCP) mod.onLCP(send); else if (mod.getLCP) mod.getLCP(send);
        if (mod.onFCP) mod.onFCP(send); else if (mod.getFCP) mod.getFCP(send);
        if (mod.onCLS) mod.onCLS(send); else if (mod.getCLS) mod.getCLS(send);
        if (mod.onFID) mod.onFID(send); else if (mod.getFID) mod.getFID(send);
        if (mod.onINP) mod.onINP(send);
    } catch (e) { /* no-op */ }
}

function trackErrors() {
    if (disabled) return;
    window.addEventListener('error', (e) => {
        enqueue('error', {
            message: e.message || (e.error && e.error.message) || 'unknown error',
            stack:   (e.error && e.error.stack) ? String(e.error.stack).slice(0, 2000) : null,
            source:  'error',
            url:     window.location.pathname,
            line:    e.lineno || null,
            col:     e.colno || null,
        });
    });
    window.addEventListener('unhandledrejection', (e) => {
        const reason = e.reason;
        const message = reason && reason.message ? reason.message : String(reason);
        enqueue('error', {
            message:  message ? message.slice(0, 500) : 'unhandled promise rejection',
            stack:    reason && reason.stack ? String(reason.stack).slice(0, 2000) : null,
            source:   'unhandledrejection',
            url:      window.location.pathname,
        });
    });
}

function trackRouteChanges() {
    if (disabled) return;
    let lastPath = window.location.pathname;
    let lastChange = performance.now();
    const fire = () => {
        const now = performance.now();
        const newPath = window.location.pathname;
        if (newPath === lastPath) return;
        enqueue('routeChange', { from: lastPath, to: newPath, duration: Math.round(now - lastChange) });
        lastPath = newPath;
        lastChange = now;
    };
    const wrap = (key) => {
        const orig = window.history[key];
        if (!orig || orig.__wf_wrapped) return;
        const wrapped = function () { const r = orig.apply(this, arguments); setTimeout(fire, 0); return r; };
        wrapped.__wf_wrapped = true;
        window.history[key] = wrapped;
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', () => setTimeout(fire, 0));
}

if (!disabled) {
    trackPageLoad();
    trackWebVitals();
    trackErrors();
    trackRouteChanges();
    window.addEventListener('beforeunload', flush);
    window.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
}

const analytics = {
    trackPageLoad,
    trackEvent: enqueue,
    flush,
    isDisabled: () => disabled,
};

export default analytics;
export { enqueue as trackEvent, flush };
